import type { ReactNode } from 'react';
import styled from 'styled-components';
import type { AdSummary } from 'types';
import { media } from 'helpers/breakpoints';

import { Link } from 'components/Toolkit/Button/Link';
import { Tag } from 'components/Toolkit/Tags/Tag';
import { CadCard } from 'components/PageWrappers/SharedComponents/CadCard/CadCard';

import convertToFriendlyPath from 'utils/FriendlyPath';
import { useCampaignURLHandler } from 'components/PageWrappers/Hooks/Campaigns/UseCampaignURLHandler';
import { accessibleOnClick } from 'utils/Accessibility';

export interface FeaturedDealerProps {
  heading?: string;
  title: string;
  listItems?: (string | undefined | ReactNode)[];
  showRoomText: string;
  showRoomUrl: string;
  dealerLogo?: string;
  ads?: AdSummary[];
  cdnUrl: string;
  domain: string;
  updateCampaignNumber?: number;
  testId?: string;
}

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const Heading = styled.h2`
  ${({ theme }) => theme.fontSize.L18};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  ${media.large} {
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }
`;

const InfoContainer = styled.div<{ count: number }>`
  padding: ${({ theme }) =>
    `0 ${theme.spacing.M16} ${theme.spacing.M16} ${theme.spacing.M16}`};
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    padding: ${({ theme }) => `0 0 ${theme.spacing.M16} 0`};
  }

  ${media.large} {
    order: 1;
    padding: ${({ theme }) => `0 ${theme.spacing.M16}`};
    margin-bottom: 0;
    width: ${({ count }) => (count === 3 ? 'calc((1 / 3) * 100%)' : '100%')};
  }
`;

const HeadingWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 64px;
  column-gap: ${({ theme }) => theme.spacing.S8};

  ${media.large} {
    padding-right: 0;
    display: block;
  }
`;

const Title = styled.p`
  ${({ theme }) => theme.fontSize.L18};
  color: ${({ theme }) => theme.colors.WHITE};
  font-weight: ${({ theme }) => theme.fontWeight.bold};

  ${media.large} {
    ${({ theme }) => theme.fontSize.L24};
  }
`;

const SubtitleList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const SubtitleItem = styled.li`
  ${({ theme }) => theme.fontSize.M14};
  color: ${({ theme }) => theme.colors.GREY_LIGHT};
  list-style: none;
  display: flex;
  align-items: center;

  &:after {
    width: ${({ theme }) => theme.spacing.S4};
    height: ${({ theme }) => theme.spacing.S4};
    background: ${({ theme }) => theme.colors.GREY_LIGHT};
    margin-left: ${({ theme }) => theme.spacing.S8};
    margin-right: ${({ theme }) => theme.spacing.S8};
    content: '';
    border-radius: 50%;
  }

  &:last-child {
    &:after {
      width: ${({ theme }) => theme.spacing.S4};
      content: none;
      height: 100%;
    }
  }
`;

const SLink = styled(Link)`
  margin-bottom: ${({ theme }) => theme.spacing.S8};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.S12};
  }
`;

const STag = styled(Tag)`
  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.S12};
  }
`;

const ImageContainer = styled.div`
  width: 64px;
  height: 32px;
  position: absolute;
  top: ${({ theme }) => theme.spacing.M16};
  right: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    right: 0;
  }

  ${media.large} {
    position: initial;
    width: 160px;
    height: 80px;
  }
`;

const Img = styled.img`
  width: 64px;
  height: 32px;
  ${media.large} {
    width: 160px;
    height: 80px;
  }
`;

const CardsContainer = styled.ul<{ count: number }>`
  display: grid;
  overflow-x: auto;
  overflow-y: hidden;
  grid-template-rows: 1fr;
  grid-auto-flow: column;
  grid-auto-columns: ${({ count }) => (count === 1 ? '1fr' : '242px')};

  column-gap: ${({ theme }) => theme.spacing.M16};
  width: 100%;
  margin-right: ${({ theme }) => `0 ${theme.spacing.M16}`};
  padding: ${({ theme }) => `0 0 ${theme.spacing.M16} ${theme.spacing.M16}`};

  &::after {
    content: '';
    width: 1px;
  }

  /* If only 1 card for small screens we want to fill width, set back to 242px width here */
  @media only screen and (min-width: 400px) {
    grid-auto-columns: 242px;
  }

  /* If 2 cards between 400px & 500px we want to fill width */
  @media only screen and (min-width: 500px) {
    grid-auto-columns: ${({ count }) => (count === 2 ? '1fr' : '242px')};
  }

  ${media.medium} {
    padding: ${({ theme }) => `0 0 ${theme.spacing.M16} 0`};
    width: 100%;
    ${({ count }) => count !== 3 && 'overflow-x:initial'};

    &::after {
      content: none;
      width: 0;
    }

    grid-auto-columns: ${({ count }) => (count === 3 ? '1fr' : '242px')};
  }

  ${media.large} {
    padding: 0;
    margin: ${({ theme }) => `0 ${theme.spacing.M16} 0 0`};
    width: ${({ count }) => (count === 3 ? 'calc((2 / 3) * 100%)' : 'initial')};
  }
`;

const CardLink = styled(Link)`
  height: 100%;
`;

function FeaturedDealer({
  heading,
  title,
  listItems,
  showRoomText,
  showRoomUrl,
  dealerLogo,
  ads,
  cdnUrl,
  domain,
  updateCampaignNumber,
  testId,
}: FeaturedDealerProps) {
  const { handleCampaignAdRouterPush } = useCampaignURLHandler();

  return (
    <>
      {ads && ads.length !== 0 && (
        <>
          {heading && (
            <SectionHeader>
              <Heading>{heading}</Heading>
            </SectionHeader>
          )}
          <Wrapper>
            <InfoContainer count={ads.length}>
              <HeadingWrapper>
                <Title>{title}</Title>
              </HeadingWrapper>
              <HeadingWrapper>
                {listItems && (
                  <SubtitleList>
                    {listItems.map(
                      (item, index) =>
                        item && <SubtitleItem key={index}>{item}</SubtitleItem>,
                    )}
                  </SubtitleList>
                )}
              </HeadingWrapper>
              <SLink
                isFakeLink
                {...accessibleOnClick(
                  () => handleCampaignAdRouterPush(showRoomUrl),
                  'link',
                )}
                ofType="SECONDARY"
              >
                {showRoomText}
              </SLink>
              <STag
                text="FEATURED"
                textColor="WHITE"
                bgColor="ORANGE"
                variant="DEFAULT"
              />
              {dealerLogo && (
                <ImageContainer>
                  <Img src={dealerLogo} alt="Dealer Logo" />
                </ImageContainer>
              )}
            </InfoContainer>
            <CardsContainer count={ads.length} data-testid={testId}>
              {ads.map((ad, index) => {
                const friendlyPath = convertToFriendlyPath(
                  domain,
                  ad.friendlyUrl!,
                  updateCampaignNumber!,
                );

                return (
                  <li key={index}>
                    <CardLink
                      isFakeLink
                      {...accessibleOnClick(
                        () => handleCampaignAdRouterPush(friendlyPath),
                        'link',
                      )}
                      ofType="SECONDARY"
                    >
                      <CadCard
                        variant="SMALL"
                        orientation="VERTICAL"
                        cdnUrl={cdnUrl}
                        {...ad}
                        hideSave={true}
                      />
                    </CardLink>
                  </li>
                );
              })}
            </CardsContainer>
          </Wrapper>
        </>
      )}
    </>
  );
}

export { FeaturedDealer };
